// 📁 src/utils/formatUtils.js
import dayjs from "dayjs";

const EN_SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const parseDateOnlyParts = (dateValue) => {
  if (!dateValue) return null;
  const s = String(dateValue).trim();
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:$|[T\s])/);
  if (!iso) return null;
  return {
    year: iso[1],
    month: iso[2].padStart(2, "0"),
    day: iso[3].padStart(2, "0"),
  };
};

// =====================
// 📌 Format Date: dd MMM yyyy
//     03 Jan 2025
// =====================
export const formatDateUS = (dateString) => {
  if (!dateString) return "-";

  const dateOnly = parseDateOnlyParts(dateString);
  if (dateOnly) {
    const month = EN_SHORT_MONTHS[Number(dateOnly.month) - 1] || dateOnly.month;
    return `${dateOnly.day} ${month} ${dateOnly.year}`;
  }

  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "-";

  const day = d.getDate().toString().padStart(2, "0");
  const month = d.toLocaleString("en-US", { month: "short" });
  const year = d.getFullYear();

  return `${day} ${month} ${year}`;
};


// =====================
// 📌 Format Date kiểu VN: dd/MM/yyyy
//     28/11/2025
// =====================
export const formatDateVN = (dateString) => {
  if (!dateString) return "-";

  const dateOnly = parseDateOnlyParts(dateString);
  if (dateOnly) {
    return `${dateOnly.day}/${dateOnly.month}/${dateOnly.year}`;
  }

  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "-";

  const day = d.getDate().toString().padStart(2, "0");
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const year = d.getFullYear();

  return `${day}/${month}/${year}`;
};

/** dd/MM/yyyy HH:mm — ví dụ: 28/11/2025 14:35 */
export const formatDateTimeVN = (dateString) => {
  if (!dateString) return "-";

  const dateOnly = parseDateOnlyParts(dateString);
  if (dateOnly && !/[T\s]\d{1,2}:\d{2}/.test(String(dateString))) {
    return `${dateOnly.day}/${dateOnly.month}/${dateOnly.year}`;
  }

  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "-";

  const day = d.getDate().toString().padStart(2, "0");
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, "0");
  const minutes = d.getMinutes().toString().padStart(2, "0");

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/** dd/MM/yyyy — parse YYYY-MM-DD trực tiếp để tránh lệch múi giờ */
export const formatDateDDMMYYYY = (dateValue) => {
  if (!dateValue) return "";
  const dateOnly = parseDateOnlyParts(dateValue);
  if (dateOnly) {
    return `${dateOnly.day}/${dateOnly.month}/${dateOnly.year}`;
  }
  const d = new Date(dateValue);
  const s = String(dateValue).trim();
  if (Number.isNaN(d.getTime())) return s;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

/** Tách họ tên VN: từ cuối = Tên, phần còn lại = Họ */
export const splitVietnameseName = (fullName) => {
  const cleaned = String(fullName || "").trim().replace(/\s+/g, " ");
  if (!cleaned) return { ho: "", ten: "" };
  const parts = cleaned.split(" ");
  const ten = parts.pop() || "";
  const ho = parts.join(" ");
  return { ho, ten };
};

/** Ngày hiện tại theo giờ máy (YYYY-MM-DD), tránh lệch ngày so với toISOString() (UTC). */
export const todayLocalISODate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// =====================
// 📌 Format Time: HH:mm
// =====================
export const formatTime = (t) => {
  if (!t) return "-";

  const p = dayjs(t.trim(), ["HH:mm:ss", "HH:mm"]);
  return p.isValid() ? p.format("HH:mm") : t;
};

// =====================
// 📌 Thêm nhiều hàm sau này
// =====================

// Ví dụ: format datetime
export const formatDateTime = (dateTime) => {
  if (!dateTime) return "-";
  return dayjs(dateTime).format("DD/MM/YYYY HH:mm");
};

// Ví dụ: format số có dấu phẩy
export const formatNumber = (num) => {
  if (num === null || num === undefined) return "-";
  return num.toLocaleString("en-US");
};
